import axios, { AxiosResponse } from 'axios';
import { getTokenPromise } from '../auth';

interface GetAllListPagesInput<F> {
    /**
     * The filter state to be applied in the query. The generic F type should refer to one of the
     * AppSync filter input types, such as {@link TableTrqkAudioFilterInput}
     */
    filter?: F;
    /**
     * The getToken method returned by useTokenModal
     */
    getToken: GetToken;
    /**
     * Optional limit for page size
     */
    limit?: number;
    /**
     * The query, expressed as a plain string (usually with template literals ``)
     */
    query: string;
    /**
     * The name of the outer query, used to look up the data from the axios response
     */
    queryName: string;
    /**
     * The URL of the GraphQL API
     */
    trqkDirectoryUrl: string;
}

// We need to use await in a loop in order to sequentially fetch all pages of results
/* eslint-disable no-await-in-loop */

/**
 * Get all pages of a paginated AppSync `list...` query, like `listTrqkAudios`. This is implemented
 * with axios because Apollo Client has issues with reliably iterating through all pages of such a
 * query.
 * @param input - {@link GetAllListPagesInput}
 * @returns a Promise that resolves to the concatenated of items from all pages returned by the API
 */
export async function getAllListPages<D, F>({
    filter,
    getToken,
    limit,
    query,
    queryName,
    trqkDirectoryUrl,
}: GetAllListPagesInput<F>): Promise<D[]> {
    let hasNextPage = true;
    let nextToken: undefined | string | null;
    let data: D[] = [];

    while (hasNextPage) {
        const token = await getTokenPromise(getToken);

        const response: AxiosResponse<{
            data: {
                [key: string]: {
                    items: D[];
                    nextToken?: Maybe<string>;
                };
            };
        }> = await axios({
            method: 'post',
            url: trqkDirectoryUrl,
            headers: {
                Authorization: token,
            },
            data: {
                query,
                variables: {
                    filter,
                    limit,
                    nextToken,
                },
            },
        });

        const currentItems: D[] =
            response?.data?.data?.[queryName]?.items ?? [];
        const currentNextToken =
            response?.data?.data?.[queryName]?.nextToken ?? null;

        data = [...data, ...currentItems];

        if (!currentNextToken) {
            hasNextPage = false;
        } else {
            nextToken = currentNextToken;
        }
    }

    return data;
}
